import { Controller } from '@hotwired/stimulus'

// Connects to data-controller="tabs"
export default class extends Controller {
  static classes = ['activeButton', 'inactiveButton', 'activeTab', 'inactiveTab', 'activeText', 'inactiveText', 'activeAction', 'inactiveAction']
  static targets = ['button', 'tab', 'panel', 'select', 'text', 'action', 'input']
  static values = {
    index: 0,
    updateAnchor: Boolean
  }

  get tabsCount () {
    return this.tabTargets.length
  }

  get anchor () {
    return (document.URL.split('#').length > 1) ? document.URL.split('#')[1] : null
  }

  connect () {
    if (this.anchor) this.indexValue = Math.max(this.tabTargets.findIndex((tab) => tab.id === this.anchor), 0)
    this.showTab()
    document.addEventListener('turbo:morph', () => this.showTab())
  }

  // Changes to the clicked tab
  change (event) {
    if (event.currentTarget.tagName === 'SELECT') {
      this.indexValue = event.currentTarget.selectedIndex

      // If target specifies an index, use that
    } else if (event.currentTarget.dataset.index) {
      this.indexValue = event.currentTarget.dataset.index

      // If target specifies an id, use that
    } else if (event.currentTarget.dataset.id) {
      this.indexValue = this.tabTargets.findIndex((tab) => tab.id === event.currentTarget.dataset.id)

      // Otherwise, use the index of the current target
    } else {
      this.indexValue = this.tabTargets.indexOf(event.currentTarget)
    }

    window.dispatchEvent(new CustomEvent('tsc:tab-change'))
  }

  nextTab () {
    this.indexValue = Math.min(this.indexValue + 1, this.tabsCount - 1)
  }

  previousTab () {
    this.indexValue = Math.max(this.indexValue - 1, 0)
  }

  firstTab () {
    this.indexValue = 0
  }

  lastTab () {
    this.indexValue = this.tabsCount - 1
  }

  indexValueChanged () {
    this.showTab()

    // Update URL with the tab ID if it has one
    // This will be automatically selected on page load
    if (this.updateAnchorValue) {
      window.location.hash = this.tabTargets[this.indexValue].id
    }
  }

  showTab () {
    this.panelTargets.forEach((panel, index) => {
      const tab = this.tabTargets[index]
      const button = this.buttonTargets[index]
      const text = this.textTargets[index]
      const action = this.actionTargets[index]

      if (index === this.indexValue) {
        panel.classList.remove('hidden')
        if (this.hasInactiveTabClass) tab?.classList?.remove(...this.inactiveTabClasses)
        if (this.hasActiveTabClass) tab?.classList?.add(...this.activeTabClasses)
        if (this.hasInactiveButtonClass) button?.classList?.remove(...this.inactiveButtonClasses)
        if (this.hasActiveButtonClass) button?.classList?.add(...this.activeButtonClasses)
        if (this.hasInactiveTextClass) text?.classList?.remove(...this.inactiveTextClasses)
        if (this.hasActiveTextClass) text?.classList?.add(...this.activeTextClasses)
        if (this.hasInactiveActionClass) action?.classList?.remove(...this.inactiveActionClasses)
        if (this.hasActiveActionClass) action?.classList?.add(...this.activeActionClasses)
      } else {
        panel.classList.add('hidden')
        if (this.hasActiveTabClass) tab?.classList?.remove(...this.activeTabClasses)
        if (this.hasInactiveTabClass) tab?.classList?.add(...this.inactiveTabClasses)
        if (this.hasActiveButtonClass) button?.classList?.remove(...this.activeButtonClasses)
        if (this.hasInactiveButtonClass) button?.classList?.add(...this.inactiveButtonClasses)
        if (this.hasActiveTextClass) text?.classList?.remove(...this.activeTextClasses)
        if (this.hasInactiveTextClass) text?.classList?.add(...this.inactiveTextClasses)
        if (this.hasActiveActionClass) action?.classList?.remove(...this.activeActionClasses)
        if (this.hasInactiveActionClass) action?.classList?.add(...this.inactiveActionClasses)
      }
    })

    if (this.hasSelectTarget) {
      this.selectTarget.selectedIndex = this.indexValue
    }

    for (const input of this.inputTargets) {
      input.value = this.indexValue
    }
  }
}
